import React, { useEffect, useState } from "react";
const API = process.env.REACT_APP_API_BASE_URL || "https://zoompay.co.ke";
const getToken = () => localStorage.getItem("adminToken");

const DISCOUNT_KEYS = [
  { key: "fx_discount_snpn",             label: "SNPN FX Markup",          desc: "Applied to all Send Now Pay Now transactions" },
  { key: "fx_discount_snpl",             label: "SNPL FX Markup",          desc: "Applied to all Send Now Pay Later transactions" },
  { key: "fx_discount_cash",             label: "Cash Remittance",          desc: "Extra FX benefit for cash delivery" },
  { key: "fx_discount_airtime",          label: "Airtime Top-Up",           desc: "Extra FX benefit for airtime" },
  { key: "fx_discount_bill_pay",         label: "Bill Pay",                 desc: "Extra FX benefit for bill payments" },
  { key: "snpl_deferred_settlement_fee", label: "SNPL Service Fee",         desc: "Fee charged on SNPL transactions" },
];

function DiscountRow({ setting, currentValue, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const pct = (v) => v != null ? (parseFloat(v) * 100).toFixed(2) + "%" : "—";

  const save = async () => {
    const num = parseFloat(val) / 100;
    if (isNaN(num) || num < 0 || num > 0.2) {
      setFeedback({ ok: false, msg: "Value must be 0%–20%" });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/v2/cockpit/fx-config/discount`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ key: setting.key, value: num, reason })
      });
      const d = await res.json();
      if (d.ok) { setFeedback({ ok: true, msg: "Updated to " + pct(num) }); setEditing(false); onSave(); }
      else setFeedback({ ok: false, msg: d.error });
    } catch(e) { setFeedback({ ok: false, msg: e.message }); }
    finally { setLoading(false); }
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="font-medium text-gray-800">{setting.label}</div>
        <div className="text-xs text-gray-400">{setting.desc}</div>
      </td>
      <td className="px-4 py-3">
        <span className="font-bold text-blue-700 text-lg">{pct(currentValue)}</span>
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <input type="number" min="0" max="20" step="0.01" value={val}
                onChange={e => setVal(e.target.value)} placeholder="2.25"
                className="w-24 border rounded-lg px-3 py-1.5 text-sm pr-6" />
              <span className="absolute right-2 top-1.5 text-gray-400 text-sm">%</span>
            </div>
            <input type="text" value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Reason..." className="border rounded-lg px-2 py-1.5 text-sm w-32" />
            <button onClick={save} disabled={loading}
              className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
              {loading ? "..." : "Save"}
            </button>
            <button onClick={() => { setEditing(false); setFeedback(null); }}
              className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-300">Cancel</button>
          </div>
        ) : (
          <button onClick={() => { setEditing(true); setVal((parseFloat(currentValue||0)*100).toFixed(2)); setFeedback(null); }}
            className="text-sm bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-100 font-medium">
            Edit
          </button>
        )}
        {feedback && (
          <span className={"ml-2 text-xs font-medium " + (feedback.ok ? "text-green-600" : "text-red-600")}>
            {feedback.ok ? "✅ " : "❌ "}{feedback.msg}
          </span>
        )}
      </td>
    </tr>
  );
}

export default function CockpitFXConfig() {
  const [rates, setRates] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [editRate, setEditRate] = useState(null);
  const [newRate, setNewRate] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/v2/cockpit/fx-config`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json())
      .then(d => { if (d.ok) { setRates(d.rates || []); setSettings(d.settings || {}); } })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const refreshRates = async () => {
    setRefreshing(true); setRefreshResult(null);
    try {
      const res = await fetch(`${API}/api/v2/cockpit/fx-rates/refresh`, {
        method: "POST", headers: { Authorization: `Bearer ${getToken()}` }
      });
      const d = await res.json();
      setRefreshResult(d);
      if (d.ok) load();
    } catch(e) { setRefreshResult({ ok: false, error: e.message }); }
    finally { setRefreshing(false); }
  };

  const saveRate = async (rate) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/v2/cockpit/fx-config/rate`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ fromCurrency: rate.fromCurrency, toCurrency: rate.toCurrency, rate: parseFloat(newRate), reason: "Manual override" })
      });
      const d = await res.json();
      if (d.ok) { setEditRate(null); setNewRate(""); load(); }
      else alert("Error: " + d.error);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-gray-400">Loading FX configuration...</div>;

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold">💱 FX Configuration</h1>
        <p className="text-gray-500 text-sm">Manage exchange rate markups and live rates. Changes take effect immediately.</p>
      </div>

      <div className="bg-white border rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-800">📊 FX Markups and Fee Rates</h2>
          <p className="text-xs text-gray-400 mt-0.5">Applied on top of base exchange rates.</p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-gray-600 font-medium">Setting</th>
              <th className="px-4 py-3 text-left text-gray-600 font-medium">Current Value</th>
              <th className="px-4 py-3 text-left text-gray-600 font-medium">Update</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {DISCOUNT_KEYS.map(s => (
              <DiscountRow key={s.key} setting={s} currentValue={settings[s.key]} onSave={load} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <div>
            <h2 className="font-semibold text-gray-800">🌍 Live Exchange Rates</h2>
            <p className="text-xs text-gray-400 mt-0.5">Source: Bank of Canada + Open Exchange Rates. Auto-refreshed daily at 06:00 UTC.</p>
          </div>
          <button onClick={refreshRates} disabled={refreshing}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {refreshing ? "Refreshing..." : "🔄 Refresh Now"}
          </button>
        </div>
        {refreshResult && (
          <div className={"mx-6 mt-4 p-3 rounded-lg text-sm " + (refreshResult.ok ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-700")}>
            {refreshResult.ok ? ("✅ " + refreshResult.updated + " rates updated successfully") : ("❌ " + refreshResult.error)}
          </div>
        )}
        <table className="w-full text-sm mt-2">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-gray-600 font-medium">Corridor</th>
              <th className="px-4 py-3 text-left text-gray-600 font-medium">Rate</th>
              <th className="px-4 py-3 text-left text-gray-600 font-medium">Last Updated</th>
              <th className="px-4 py-3 text-left text-gray-600 font-medium">Override</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rates.map(r => (
              <tr key={r.id || (r.fromCurrency+r.toCurrency)} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{r.fromCurrency} → {r.toCurrency}</td>
                <td className="px-4 py-3 font-bold text-gray-800">
                  1 {r.fromCurrency} = {parseFloat(r.rate).toLocaleString("en-CA", { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {r.toCurrency}
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">{new Date(r.updatedAt).toLocaleString("en-CA")}</td>
                <td className="px-4 py-3">
                  {editRate && editRate.fromCurrency === r.fromCurrency && editRate.toCurrency === r.toCurrency ? (
                    <div className="flex items-center gap-2">
                      <input type="number" step="0.0001" value={newRate} onChange={e => setNewRate(e.target.value)}
                        placeholder={r.rate} className="w-28 border rounded px-2 py-1 text-sm" />
                      <button onClick={() => saveRate(r)} disabled={saving}
                        className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 disabled:opacity-50">
                        {saving ? "..." : "Save"}
                      </button>
                      <button onClick={() => { setEditRate(null); setNewRate(""); }}
                        className="bg-gray-200 text-gray-600 px-2 py-1 rounded text-xs">Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditRate(r); setNewRate(parseFloat(r.rate).toFixed(4)); }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium">Override</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
